<script lang="ts">
  // Duelist creator, styled as a cosmetics catalog. Left: live preview of the
  // ENLARGED "main" bust (the menu/customization art). Right: a category rail + a
  // grid of selectable part "products" (rendered from the enlarged art) + a colour
  // row. The selection is keyed by the shared {mnemonic,variant,colour} grammar,
  // so the same saved look drives this bust AND the small chibi that walks the
  // map. Saves a ChibiLook via AvatarSet, the world + HUD then render it.
  import { onMount } from "svelte";
  import { fly } from "svelte/transition";
  import { Application } from "pixi.js";
  import { PortraitAvatar, type PortraitSelection } from "../../render/PortraitAvatar.ts";
  import { asset, loadWorldData } from "../../data.ts";
  import { session } from "../stores/session.svelte.ts";
  import { send } from "../stores/net.svelte.ts";
  import type { PortraitManifest, PortraitSlot } from "@ygoevo/yo2-data";
  import type { ChibiLook } from "@ygoevo/world-protocol";
  import DataWindow from "../components/ui/DataWindow.svelte";
  import NeonButton from "../components/ui/NeonButton.svelte";
  import SleeveSwatch from "../components/duel/SleeveSwatch.svelte";
  import { SLEEVES } from "../duel/sleeves.ts";
  import { DEFAULT_SLEEVE_ID, DEFAULT_DUELDISK_SKU, BETA_GOLD_DUELDISK_SKU, DUEL_DISKS, OUTFITS, duelDiskSkuFor, duelDiskColorRarity, findDuelDisk, uniformSchoolsForVariant, DEFAULT_OUTFIT_SKUS, findOutfit, outfitSkuFor, hairLocked, hoodForOutfit } from "@ygoevo/world-protocol";
  import { RARITY_BORDER, RARITY_BORDER_REST, RARITY_BG, type CosmeticRarity } from "../cosmetic-rarity.ts";

  // Gacha tiers (outfits) → cosmetic tiers (disks/UI), so one rarity-colour scheme covers both.
  const GACHA_TO_COSMETIC: Record<string, CosmeticRarity> = { common: "common", rare: "rare", super: "epic", ultra: "legendary" };

  // Reused in two contexts: a dismissible overworld overlay, and the required first-run route.
  // In required mode the close affordance is absent and onClose means successful completion.
  let { onClose, required = false }: { onClose: () => void; required?: boolean } = $props();

  // GX dorm uniforms (the `ac` slot) unlock by rank; everything else is freely chosen.
  // Locked uniforms simply don't appear in the catalog until earned (no greyed items).
  const ownedUniforms = $derived(new Set(session.loginOk?.ownedUniforms ?? []));

  // ── card sleeve (collectible cosmetic) ──
  let sleeve = $state(session.loginOk?.avatar.sleeveId ?? DEFAULT_SLEEVE_ID);
  const ownedSleeves = $derived(new Set(session.loginOk?.ownedSleeves ?? [DEFAULT_SLEEVE_ID]));
  // Rarity colours follow the app tokens (app.css: epic = purple, legendary = orange).
  const RARITY: Record<string, string> = { common: "text-ink-dim", rare: "text-cyan", epic: "text-purple", legendary: "text-orange" };

  // ── duel disk (collectible avatar gear, the painted `dd`/`da` portrait slots) ──
  // Per-SKU ownership (`<silhouette>_c<color>`). A silhouette is "owned" if the player owns
  // any of its colourways; an individual colour is owned only via its specific SKU.
  const ownedDuelDiskSkus = $derived(new Set(session.loginOk?.ownedDuelDisks ?? [DEFAULT_DUELDISK_SKU]));
  const ownedDiskSilhouettes = $derived(
    new Set([...ownedDuelDiskSkus].map((sku) => findDuelDisk(sku)?.def.id).filter((x): x is string => !!x)),
  );

  // ── outfit (the `dc` portrait slot, bought per colourway, like duel disks) ──
  // Only the default outfit (all its colours) is owned from the start; every other outfit's
  // colours are bought in the Fashion shop. Same per-SKU model as disks.
  const ownedOutfitSkus = $derived(new Set(session.loginOk?.ownedOutfits ?? DEFAULT_OUTFIT_SKUS));
  const ownedOutfitSilhouettes = $derived(
    new Set([...ownedOutfitSkus].map((sku) => findOutfit(sku)?.def.id).filter((x): x is string => !!x)),
  );

  /** Colour indices the player owns for a gated silhouette, disks ("dd_03") and outfits
   *  ("dc_02") alike ("dd_03" → Set([1, 2])). */
  function ownedColorsForSilhouette(silhouetteId: string): Set<number> {
    const out = new Set<number>();
    if (silhouetteId.startsWith("dc")) {
      for (const sku of ownedOutfitSkus) {
        const e = findOutfit(sku);
        if (e && e.def.id === silhouetteId) out.add(e.c.color);
      }
    } else {
      for (const sku of ownedDuelDiskSkus) {
        const e = findDuelDisk(sku);
        if (e && e.def.id === silhouetteId) out.add(e.c.color);
      }
    }
    return out;
  }

  // Rarity lookups, only disks carry a rarity today, so other slots (hair, eyes, …)
  // return null and skip the tier ring. The gold colourway returns "legendary" via
  // duelDiskColorRarity, so a gold I own shows the same orange ring as a legendary tag.
  function silhouetteRarity(slot: string, variant: string): CosmeticRarity | null {
    if (DISK_SLOTS.includes(slot)) return DUEL_DISKS.find((d) => d.slot === slot && d.variant === variant)?.rarity ?? null;
    if (slot === "dc" || slot === "ac") {
      const o = OUTFITS.find((x) => x.id === `${slot}_${variant}`);
      return o ? GACHA_TO_COSMETIC[o.rarity] ?? null : null;
    }
    return null;
  }
  function colourRarity(slot: string, variant: string, color: number): CosmeticRarity | null {
    if (!DISK_SLOTS.includes(slot)) return null;
    const def = DUEL_DISKS.find((d) => d.slot === slot && d.variant === variant);
    const c = def?.colors.find((c) => c.color === color);
    return def && c ? duelDiskColorRarity(def, c) : null;
  }

  // The two top-layer disk slots, treated as ONE mutually-exclusive "Duel Disk" picker.
  const DISK_SLOTS = ["dd", "da"];
  // Slots whose colours are gated by per-SKU ownership (disks + outfits): the colour row only
  // offers owned colourways, and equipping clamps to an owned colour.
  const OWNED_COLOR_SLOTS = ["dd", "da", "dc"];

  const PREVIEW = 400; // Pixi native render size; the canvas is CSS-scaled to fill its column.
  // Eye detail (`es`) is a supporting layer of the selected eyes, not a choice of
  // its own. It is derived from `ey` everywhere below, just like back/style hair
  // are derived from front hair.
  const EXCLUDE = new Set<string>(["es"]);
  // Preferred rail order (any manifest slot not listed is appended after). "dd" leads the merged
  // Duel Disk tab (da folds into it).
  const ORDER = ["h1", "hs", "h2", "eb", "ey", "no", "mo", "dc", "ac", "dd", "bo", "a1", "a2"];
  const ICONS: Record<string, string> = {
    h1: "✦", hs: "✧", h2: "❰", eb: "︵", ey: "◉", no: "▾",
    mo: "‿", dc: "▣", ac: "◈", disk: "⬢", bo: "✶", a1: "✚", a2: "♛",
  };
  const PREVIEW_DIRECTIONS = [
    { mirrored: false, label: "◀ Face left" },
    { mirrored: true, label: "Face right ▶" },
  ];

  const savedGender = (session.loginOk?.avatar.chibi?.gender ?? "cm") as "cm" | "cw";

  // Name is bound to the player's Discord account (set server-side at login), not
  // editable here; we just echo it back in the AvatarSet payload.
  const name = session.loginOk?.avatar.name ?? "Duelist";

  let manifest = $state<PortraitManifest | null>(null);
  let gender = $state<"cm" | "cw">(savedGender);
  let mirrored = $state(false);
  let slots = $state<PortraitSelection>({});
  let activeCat = $state("hair");
  let ready = $state(false);
  // World data didn't load (e.g. the world server was unreachable). Without this the
  // catalog silently collapses to just the sleeve tab + a blank preview, show a retry instead.
  let loadErr = $state(false);

  // ── manifest-driven catalog ─────────────────────────────────────────────────
  function genderSlots(): PortraitSlot[] {
    return manifest?.genders[gender]?.slots ?? [];
  }
  function slotDef(key: string): PortraitSlot | undefined {
    return genderSlots().find((s) => s.slot === key);
  }
  function variantsFor(key: string): string[] {
    const s = slotDef(key);
    const vs = (s?.variants ?? []).map((v) => v.variant);
    return s?.optional ? ["00", ...vs] : vs;
  }
  function colorsFor(key: string, variant: string): number[] {
    if (variant === "00") return [1];
    return slotDef(key)?.variants.find((v) => v.variant === variant)?.colors ?? [1];
  }
  /** Enlarged-art thumbnail for one slot/variant/colour (matches the live bust). */
  function thumb(key: string, variant: string, color: number): string {
    if (variant === "00") return "";
    const s = slotDef(key);
    const v = s?.variants.find((x) => x.variant === variant);
    if (!s || !v) return "";
    const byColor = v.cels[String(color)] ?? v.cels[String(v.colors[0])];
    if (!byColor) return "";
    const p = byColor.r ?? byColor.n;
    return p ? asset(p) : "";
  }
  /** How much to zoom a tile's cel so the part actually reads. Facial features are wide, short strips
   *  (eyes are 768×88), which `object-contain` shrinks to a ~16px sliver in the tile — the "tiny
   *  thumbnails" bug. The feature is horizontally centred in the cel, so scaling from the centre fills
   *  the tile with it and crops the transparent sides. Tall parts (hair/outfit/disk, rect h ≥ 130)
   *  already fill the tile, so they stay at 1×. */
  function tileZoom(key: string, variant: string): number {
    if (variant === "00") return 1;
    const h = slotDef(key)?.variants.find((v) => v.variant === variant)?.rect?.[3] ?? 768;
    if (h >= 130) return 1;
    return Math.min(6, Math.max(1, (0.42 * 768) / h)); // aim the feature band at ~42% of tile height
  }

  function tileTransform(zoom: number): string {
    return `transform:scaleX(${mirrored ? -1 : 1}) scale(${zoom})`;
  }

  /** Which colour a catalog tile previews: the equipped colour when selected, else the first colour
   *  you OWN for a gated slot (so a disk you own only in gold shows gold, not the default), else the
   *  silhouette's first colour. */
  function tileColor(key: string, variant: string, selected: boolean): number {
    if (selected) return slots[key]?.color ?? 1;
    if (OWNED_COLOR_SLOTS.includes(key)) {
      const owned = [...ownedColorsForSilhouette(`${key}_${variant}`)].sort((a, b) => a - b);
      if (owned.length) return owned[0]!;
    }
    return colorsFor(key, variant)[0] ?? 1;
  }
  /** Parts gated by ownership: GX dorm uniforms (`ac`, earned by rank) and duel disks (`dd`/`da`,
   *  bought with DP). Everything else is free. Locked items simply don't appear until earned/bought. */
  function locked(key: string, variant: string): boolean {
    if (variant === "00") return false;
    if (key === "h1") {
      // Hoods (h1_98/h1_99) unlock with their cloak; collectible hairs (Kuriboh h1_14) stay locked;
      // every other hair is free.
      return hairLocked(variant, (cloakId) => ownedOutfitSilhouettes.has(cloakId));
    }
    if (DISK_SLOTS.includes(key)) {
      // The silhouette is unlocked if the player owns any colourway of it.
      return !ownedDiskSilhouettes.has(`${key}_${variant}`);
    }
    if (key === "dc") {
      // Outfits are bought per colourway too, unlocked if you own any colour of the silhouette.
      return !ownedOutfitSilhouettes.has(`dc_${variant}`);
    }
    if (key !== "ac") return false;
    const schools = uniformSchoolsForVariant(gender, variant);
    // ac doubles as the rank-uniform slot: a uniform variant is earned by rank; everything else is
    // a costume bought in the Fashion shop (same per-silhouette ownership as outfits/disks).
    if (!schools.length) return !ownedOutfitSilhouettes.has(`ac_${variant}`);
    return !schools.some((s) => ownedUniforms.has(s));
  }

  // Merged catalog tabs. Three hair slots (front/style/back) collapse into one
  // "Hair" picker, and outfit + costume into one "Outfit" picker. Any manifest
  // slot listed here is absorbed into the named virtual tab instead of getting
  // its own.
  const HAIR_MEMBERS = ["h1", "hs", "h2"]; // front / style / back hair
  const HAIR_LEAD = "h1"; // required front-hair set; a superset of the other two
  const OUTFIT_SLOTS = ["dc", "ac"]; // outfit + costume, mutually exclusive
  const ABSORB: Record<string, { key: string; label: string; icon: string }> = {
    h1: { key: "hair", label: "Hair", icon: ICONS.h1! },
    hs: { key: "hair", label: "Hair", icon: ICONS.h1! },
    h2: { key: "hair", label: "Hair", icon: ICONS.h1! },
    dc: { key: "outfit", label: "Outfit", icon: ICONS.dc! },
    ac: { key: "outfit", label: "Outfit", icon: ICONS.dc! },
    dd: { key: "disk", label: "Duel Disk", icon: ICONS.disk! },
    da: { key: "disk", label: "Duel Disk", icon: ICONS.disk! },
  };

  function buildCats(): { key: string; label: string; icon: string; optional: boolean }[] {
    const all = genderSlots().filter((s) => !EXCLUDE.has(s.slot));
    const byKey = new Map(all.map((s) => [s.slot, s]));
    const out: { key: string; label: string; icon: string; optional: boolean }[] = [];
    const seen = new Set<string>();
    const push = (key: string, label: string, icon: string, optional: boolean) => {
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ key, label, icon, optional });
    };
    const consider = (s: PortraitSlot) => {
      const v = ABSORB[s.slot];
      if (v) push(v.key, v.label, v.icon, false);
      else push(s.slot, s.label, ICONS[s.slot] ?? "✦", s.optional);
    };
    for (const k of ORDER) {
      const s = byKey.get(k);
      if (s) consider(s);
    }
    for (const s of all) consider(s); // append any slot not in ORDER (deduped by `seen`)
    return out;
  }

  // A selectable catalog tile, tagged with the manifest slot it actually drives
  // (so a merged tab can mix slots: outfit `dc` + costume `ac`, or the three
  // hair layers behind one "Hair" pick).
  type GridItem = { slot: string; variant: string };
  function itemsFor(cat: string): GridItem[] {
    if (cat === "hair") {
      // One coherent hairstyle per tile, keyed by the required front-hair set
      // (a superset of the style/back indices). Picking fans out to all 3 slots.
      return variantsFor(HAIR_LEAD)
        .filter((v) => v !== "00" && !locked(HAIR_LEAD, v)) // hide hood hairs you haven't earned (cloak-locked)
        .map((v) => ({ slot: HAIR_LEAD, variant: v }));
    }
    if (cat === "outfit") {
      // Outfits (dc) + costumes/uniforms (ac) in one tab, mutually exclusive
      // full-body looks. No "None" (an outfit is required); rank-locked uniforms
      // (gated on the ac slot) stay hidden until earned.
      const out: GridItem[] = [];
      for (const slot of OUTFIT_SLOTS)
        for (const v of variantsFor(slot))
          if (v !== "00" && !locked(slot, v)) out.push({ slot, variant: v });
      return out;
    }
    if (cat === "disk") {
      // The disk silhouettes (dd + da designs) in one mutually-exclusive tab. A disk is required
      // (everyone owns the basic colourway), so no "None"; silhouettes the player owns NO colour
      // of stay hidden until bought.
      const out: GridItem[] = [];
      for (const def of DUEL_DISKS)
        if (ownedDiskSilhouettes.has(def.id)) out.push({ slot: def.slot, variant: def.variant });
      return out;
    }
    return variantsFor(cat)
      .filter((v) => !locked(cat, v))
      .map((v) => ({ slot: cat, variant: v }));
  }

  // The customizer is the player's cosmetic INVENTORY, show only what they've
  // unlocked, never a locked/greyed item (those live in the shop / rank rewards
  // until earned). Sleeves filter to owned; rank-gated uniform variants drop out.
  const visibleSleeves = $derived(SLEEVES.filter((s) => ownedSleeves.has(s.id)));
  /** A category only earns space when it offers more than one owned/available item. Disk and
   *  outfit colourways are individually owned SKUs, so count their usable colourways rather than
   *  only their silhouette tiles. `00` is the synthetic "None" tile, not an item. */
  function categoryItemCount(cat: string): number {
    if (cat === "sleeve") return visibleSleeves.length;
    if (cat === "disk") {
      const visible = new Set(itemsFor(cat).map((item) => `${item.slot}_${item.variant}`));
      return DUEL_DISKS
        .filter((def) => visible.has(def.id))
        .reduce((total, def) => total + def.colors.filter((color) => ownedDuelDiskSkus.has(color.sku)).length, 0);
    }
    if (cat === "outfit") {
      return itemsFor(cat).reduce((total, item) => {
        const def = OUTFITS.find((outfit) => outfit.id === `${item.slot}_${item.variant}`);
        if (!def) return total;
        const schools = uniformSchoolsForVariant(gender, item.variant);
        if (item.slot === "ac" && schools.length)
          return total + (schools.some((school) => ownedUniforms.has(school)) ? 1 : 0);
        return total + def.colors.filter((color) => ownedOutfitSkus.has(color.sku)).length;
      }, 0);
    }
    return itemsFor(cat).filter((item) => item.variant !== "00").length;
  }
  const CATS = $derived([
    ...buildCats(),
    { key: "sleeve", label: "Card Sleeve", icon: "🂠", optional: false },
  ].filter((cat) => categoryItemCount(cat.key) > 1));
  const activeMeta = $derived(CATS.find((c) => c.key === activeCat) ?? CATS[0]);
  const gridItems = $derived(itemsFor(activeCat));

  // Gender changes can alter which manifest categories have more than one choice. Never leave the
  // rail pointing at a category that has just disappeared.
  $effect(() => {
    if (!manifest) return;
    const first = CATS[0]?.key;
    if (first && !CATS.some((cat) => cat.key === activeCat)) activeCat = first;
  });

  function isItemSel(item: GridItem): boolean {
    if (activeCat === "hair") return (slots[HAIR_LEAD]?.variant ?? "") === item.variant;
    return (slots[item.slot]?.variant ?? "00") === item.variant;
  }

  // Which slot's colours the colour row edits, and the current choice there. For
  // hair it's the front-hair set; for outfit it's whichever of dc/ac is worn.
  const colorSlot = $derived(
    activeCat === "hair"
      ? HAIR_LEAD
      : activeCat === "outfit"
        ? (slots.ac?.variant ?? "00") !== "00"
          ? "ac"
          : "dc"
        : activeCat === "disk"
          ? (slots.da?.variant ?? "00") !== "00"
            ? "da"
            : "dd"
          : activeCat,
  );
  const colorChoice = $derived(slots[colorSlot] ?? { variant: "00", color: 1 });
  const colorOptions = $derived.by(() => {
    const all = colorsFor(colorSlot, colorChoice.variant);
    // Disks + outfits gate colours by per-SKU ownership; everything else lets the player free-pick
    // any colour their look supports.
    if (!OWNED_COLOR_SLOTS.includes(colorSlot) || colorChoice.variant === "00") return all;
    const owned = ownedColorsForSilhouette(`${colorSlot}_${colorChoice.variant}`);
    return all.filter((c) => owned.has(c));
  });

  /** Default look for the current gender, seeded from any saved selection. */
  function initialSlots(): PortraitSelection {
    const out: PortraitSelection = {};
    const saved = session.loginOk?.avatar.chibi?.slots ?? {};
    for (const s of genderSlots()) {
      const vs = s.variants.map((v) => v.variant);
      const sv = saved[s.slot];
      if (sv && (sv.variant === "00" || vs.includes(sv.variant))) {
        out[s.slot] = { variant: sv.variant, color: sv.color };
      } else {
        out[s.slot] = s.optional ? { variant: "00", color: 1 } : { variant: vs[0] ?? "01", color: 1 };
      }
    }
    // Back/style hair follow the front-hair 'do: a default or legacy look may have stored
    // only h1, so sync h2/hs to it (matches pickHair) — the full hair then persists on save.
    const h1 = out.h1;
    if (h1) {
      for (const sub of ["hs", "h2"] as const) {
        const has = slotDef(sub)?.variants.some((v) => v.variant === h1.variant);
        out[sub] = has ? { variant: h1.variant, color: clampColor(sub, h1.variant, h1.color) } : { variant: "00", color: 1 };
      }
    }
    syncEyeDetail(out);
    reconcileOutfit(out); // heal any legacy save that stacked outfit + costume
    reconcileDisk(out); // guarantee exactly one (owned) duel disk, default the basic one
    return out;
  }

  // The Outfit (dc) and Costume (ac) slots are both full-body outfits, drawn one over the
  // other, so wearing both stacks them (the default jacket sat on top of a chosen costume).
  // Treat them as mutually exclusive: the one you just picked replaces the other.
  function restoreDefaultOutfit() {
    if ((slots.dc?.variant ?? "00") !== "00") return;
    const dcDefault = variantsFor("dc").find((v) => v !== "00");
    if (dcDefault) slots.dc = { variant: dcDefault, color: 1 };
  }
  /** On load / gender-swap: never keep both an outfit AND a costume (costume wins); never end
   *  up with neither (restore the default outfit so you're not down to bare skin). */
  /** Does the player own/has earned this Outfit-tab look, a dc outfit or an ac costume/uniform? */
  function ownsOutfitLook(slot: "dc" | "ac", variant: string, color: number): boolean {
    if (variant === "00") return true;
    if (slot === "ac") {
      const schools = uniformSchoolsForVariant(gender, variant);
      if (schools.length) return schools.some((s) => ownedUniforms.has(s)); // earned rank uniform
    }
    const sku = outfitSkuFor(slot, variant, color);
    return !!sku && ownedOutfitSkus.has(sku);
  }

  function reconcileOutfit(sel: PortraitSelection) {
    // Drop an outfit/costume the player doesn't own, a stale save can't keep a now-gated look.
    // The default-outfit restore below then re-dresses them if they'd end up bare.
    if (sel.dc && sel.dc.variant !== "00" && !ownsOutfitLook("dc", sel.dc.variant, sel.dc.color))
      sel.dc = { variant: "00", color: 1 };
    if (sel.ac && sel.ac.variant !== "00" && !ownsOutfitLook("ac", sel.ac.variant, sel.ac.color))
      sel.ac = { variant: "00", color: 1 };
    const acOn = (sel.ac?.variant ?? "00") !== "00";
    const dcOn = (sel.dc?.variant ?? "00") !== "00";
    if (acOn && dcOn) {
      sel.dc = { variant: "00", color: 1 };
    } else if (!acOn && !dcOn) {
      const e = findOutfit(DEFAULT_OUTFIT_SKUS[0]!)!;
      sel.dc = { variant: e.def.variant, color: e.c.color };
    }
  }

  /** Duel disks (dd/da) are one mutually-exclusive slot and everyone wears one: never keep both,
   *  drop any unowned (silhouette OR colourway) selection, and if you'd end up bare-handed equip
   *  the basic default SKU the player always owns. */
  function reconcileDisk(sel: PortraitSelection) {
    const owns = (slot: string) => {
      const s = sel[slot];
      if (!s || s.variant === "00") return false;
      const sku = duelDiskSkuFor(slot, s.variant, s.color);
      return !!sku && ownedDuelDiskSkus.has(sku);
    };
    const ddOn = owns("dd"), daOn = owns("da");
    if (!ddOn) sel.dd = { variant: "00", color: 1 };
    if (!daOn || ddOn) sel.da = { variant: "00", color: 1 }; // both → keep dd; neither handled below
    if (!ddOn && !daOn) {
      const e = findDuelDisk(DEFAULT_DUELDISK_SKU)!;
      sel[e.def.slot] = { variant: e.def.variant, color: e.c.color };
    }
  }

  function clampColor(slot: string, variant: string, color: number): number {
    const cs = colorsFor(slot, variant);
    return cs.includes(color) ? color : cs[0] ?? 1;
  }
  /** Eye detail is the skin-toned extension shipped with an eye shape. Match its
   *  variant to Eyes and its colour to Body; clear it only if no matching layer exists. */
  function syncEyeDetail(sel: PortraitSelection) {
    const eye = sel.ey;
    const detail = slotDef("es");
    const has = eye && eye.variant !== "00" && detail?.variants.some((v) => v.variant === eye.variant);
    sel.es = has
      ? { variant: eye.variant, color: clampColor("es", eye.variant, sel.bo?.color ?? 1) }
      : { variant: "00", color: 1 };
  }
  /** Like clampColor but also gates by per-SKU ownership (disks only). Keeps the requested
   *  colour if owned; otherwise picks the first owned colour for the silhouette. Used when
   *  equipping a disk so the preview never lands on a colourway you don't have (e.g. picking
   *  the Battle Disk when you only own its gold colourway must default to gold, not silver). */
  function clampOwnedColor(slot: string, variant: string, color: number): number {
    if (!OWNED_COLOR_SLOTS.includes(slot) || variant === "00") return clampColor(slot, variant, color);
    const owned = ownedColorsForSilhouette(`${slot}_${variant}`);
    if (owned.has(color)) return color;
    const first = [...owned].sort((a, b) => a - b)[0];
    return first ?? clampColor(slot, variant, color);
  }
  function pick(key: string, variant: string) {
    if (locked(key, variant)) return;
    slots[key] = { variant, color: clampOwnedColor(key, variant, slots[key]?.color ?? 1) };
    if (key === "ac" && variant !== "00") slots.dc = { variant: "00", color: 1 };
    else if (key === "dc" && variant !== "00") slots.ac = { variant: "00", color: 1 };
    else if (key === "ac" && variant === "00") restoreDefaultOutfit();
    // Disks are mutually exclusive, equipping one clears the other slot.
    else if (key === "dd" && variant !== "00") slots.da = { variant: "00", color: 1 };
    else if (key === "da" && variant !== "00") slots.dd = { variant: "00", color: 1 };
    // Hooded cloaks come as a SET, equipping one auto-equips its hood (you own it via the cloak).
    if (key === "dc" && variant !== "00") {
      const hood = hoodForOutfit(`dc_${variant}`);
      if (hood) pickHair(hood);
    }
  }
  /** Equip a whole hairstyle: front hair always, plus the matching style/back
   *  layers when that 'do ships them (else cleared), keeping one shared colour. */
  function pickHair(variant: string) {
    const color = slots[HAIR_LEAD]?.color ?? 1;
    for (const slot of HAIR_MEMBERS) {
      const def = slotDef(slot);
      if (!def) continue;
      const has = def.variants.some((v) => v.variant === variant);
      slots[slot] =
        slot === HAIR_LEAD || has
          ? { variant, color: clampColor(slot, variant, color) }
          : { variant: "00", color: 1 };
    }
  }
  function pickEyes(variant: string) {
    pick("ey", variant);
    syncEyeDetail(slots);
  }
  function pickItem(item: GridItem) {
    if (activeCat === "hair") pickHair(item.variant);
    else if (activeCat === "ey") pickEyes(item.variant);
    else pick(item.slot, item.variant);
  }
  function setColor(key: string, color: number) {
    slots[key] = { ...slots[key]!, color };
  }
  /** Apply one colour across the active tab. Hair recolours all worn layers (each
   *  clamped to what it supports); everything else just recolours its own slot. */
  function setActiveColor(color: number) {
    if (activeCat === "hair") {
      for (const slot of HAIR_MEMBERS) {
        const cur = slots[slot];
        if (!cur || cur.variant === "00") continue;
        slots[slot] = { ...cur, color: clampColor(slot, cur.variant, color) };
      }
    } else {
      setColor(colorSlot, color);
      if (activeCat === "bo") syncEyeDetail(slots);
    }
  }

  function setGender(g: "cm" | "cw") {
    if (g === gender) return;
    gender = g;
    // Re-clamp every slot to the new gender's available variants/colours.
    const next: PortraitSelection = {};
    for (const s of genderSlots()) {
      const vs = s.variants.map((v) => v.variant);
      let v = slots[s.slot]?.variant ?? (s.optional ? "00" : vs[0]!);
      if (v !== "00" && !vs.includes(v)) v = s.optional ? "00" : vs[0]!;
      const colors = colorsFor(s.slot, v);
      const c = colors.includes(slots[s.slot]?.color ?? 1) ? slots[s.slot]!.color : colors[0]!;
      next[s.slot] = { variant: v, color: c };
    }
    syncEyeDetail(next);
    reconcileOutfit(next);
    reconcileDisk(next);
    slots = next;
  }

  // ── live Pixi preview (enlarged bust) ────────────────────────────────────────
  let host: HTMLDivElement;
  let app: Application | null = null;
  let av: PortraitAvatar | null = null;

  function recenter() {
    if (!av) return;
    av.root.x = (PREVIEW - av.displayWidth) / 2;
    av.root.y = 0;
  }

  // `{ removeView: true }`, NOT `true`: `app.destroy(true)` calls Pixi's
  // GlobalResourceRegistry.release(), wiping the GLOBAL pools shared by every live Pixi app,
  // which would blank a still-mounted sibling canvas. removeView frees only this renderer + its
  // canvas. The try/catch is a backstop against any teardown throw.
  function destroyApp() {
    try {
      app?.destroy({ removeView: true });
    } catch (e) {
      console.error("[CharCreate] destroy failed (ignored)", e);
    }
    app = null;
  }

  $effect(() => {
    // re-run when the look changes
    void [ready, gender, mirrored, JSON.stringify(slots)];
    if (av) {
      av.set({ selection: $state.snapshot(slots) as PortraitSelection, gender, mirrored });
      recenter();
    }
  });

  let disposed = false;

  // Load world data, clamp the look to it, and build the live preview. Safe to retry:
  // the Pixi app/avatar are created once, so a retry after a failed fetch just fills them in.
  async function boot() {
    loadErr = false;
    let data: Awaited<ReturnType<typeof loadWorldData>>;
    try {
      data = await loadWorldData();
    } catch (e) {
      console.error("[CharCreate] world data failed to load", e);
      loadErr = true;
      return;
    }
    if (disposed) return;
    manifest = data.avatarsPortrait;
    // Clamp to a real manifest gender. A legacy/empty saved gender (anything other than the
    // manifest's "cm"/"cw") would make genderSlots() empty and collapse the whole catalog.
    if (!manifest.genders[gender]) {
      gender = ((Object.keys(manifest.genders)[0] as "cm" | "cw") ?? "cm");
    }
    slots = initialSlots();
    if (!app) {
      app = new Application();
      await app.init({
        width: PREVIEW,
        height: PREVIEW,
        backgroundAlpha: 0,
        antialias: true,
        preference: "webgpu",
        resolution: Math.min(window.devicePixelRatio || 1, 2), // crisp when CSS-scaled up on 4K
        autoDensity: true,
      });
      if (disposed) {
        destroyApp();
        return;
      }
      // Fill the (responsive) host so the fixed-resolution render scales to the column width.
      app.canvas.style.width = "100%";
      app.canvas.style.height = "100%";
      host.appendChild(app.canvas);
    }
    if (!av) {
      av = new PortraitAvatar({
        manifest,
        gender,
        selection: $state.snapshot(slots) as PortraitSelection,
        displayHeight: PREVIEW,
        mirrored,
      });
      await av.build();
      recenter();
      app.stage.addChild(av.root);
    }
    ready = true;
  }

  onMount(() => {
    void boot();
    return () => {
      disposed = true;
      destroyApp();
    };
  });

  // ── save ────────────────────────────────────────────────────────────────────
  function save() {
    if (!ready) return;
    const look: ChibiLook = { gender, slots: { ...$state.snapshot(slots) } };
    const avatar = {
      parts: session.loginOk?.avatar.parts ?? [1, 1, 1, 0, 1, 1, 1, 1, 1, 1, 1, 1],
      name: name.trim() || "Duelist",
      chibi: look,
      sleeveId: sleeve,
    };
    send({ t: "AvatarSet", selection: avatar });
    if (session.loginOk) session.loginOk = { ...session.loginOk, avatar, needsCharacter: false };
    onClose();
  }
</script>

<div class="character-root fixed inset-0 z-40 grid place-items-center overflow-y-auto scroll-cyber bg-void/95 p-[clamp(0.75rem,3vw,2.5rem)]">
  <div class="character-shell flex w-[min(2040px,93vw)] flex-col gap-4 xl:gap-6">
    <header class="character-header flex items-end justify-between">
      <h1 class="font-display text-3xl font-bold uppercase tracking-[0.16em] text-ink text-glow">{required ? "Create Your Duelist" : "Customize Duelist"}</h1>
      {#if !required}
        <button onclick={onClose} class="min-h-11 px-2 font-display text-xs uppercase tracking-[0.2em] text-ink-dim transition-colors hover:text-cyan">✕ Cancel</button>
      {/if}
    </header>

    <div class="character-grid grid gap-4 xl:gap-6 lg:grid-cols-[clamp(300px,19vw,480px)_1fr]">
      <!-- ── Preview + identity ───────────────────────────────────────── -->
      <aside in:fly={{ x: -24, duration: 400, opacity: 0 }} class="character-sidebar flex flex-col gap-4">
        <DataWindow c={16}>
          <div class="preview-content flex flex-col items-center gap-3 px-5 py-6">
            <span class="font-display text-xs uppercase tracking-[0.3em] text-ink-dim">Preview</span>
            <div
              class="preview-stage grid aspect-square w-full place-items-center overflow-hidden"
              style="background:radial-gradient(circle at 50% 34%, rgba(25,224,255,0.14), transparent 70%)"
            >
              <div bind:this={host} class="size-full"></div>
            </div>
            <div class="flex gap-1.5">
              {#each PREVIEW_DIRECTIONS as o (o.label)}
                <button
                  onclick={() => (mirrored = o.mirrored)}
                  class="chamfer-btn px-3 py-1.5 font-display text-xs font-semibold uppercase tracking-wider transition-colors {mirrored ===
                  o.mirrored
                    ? 'bg-surface-2 text-cyan glow-cyan'
                    : 'bg-void-2/50 text-ink-dim hover:text-ink'}"
                  style="--c:6px">{o.label}</button>
              {/each}
            </div>
          </div>
        </DataWindow>

        <DataWindow c={14}>
          <div class="identity-content flex flex-col gap-4 px-5 py-5">
            <div>
              <span class="mb-1.5 block font-display text-xs font-semibold uppercase tracking-[0.14em] text-cyan">Duelist</span>
              <div class="chamfer-btn flex items-center gap-2 bg-void-2/60 px-3 py-2" style="--c:7px">
                <span class="font-display text-sm text-ink">{name}</span>
                <span class="ml-auto font-display text-[10px] uppercase tracking-widest text-ink-dim">Discord</span>
              </div>
            </div>
            <div>
              <span class="mb-1.5 block font-display text-xs font-semibold uppercase tracking-[0.14em] text-cyan">Body</span>
              <div class="grid grid-cols-2 gap-1.5">
                {#each [{ g: "cm", label: "Male" }, { g: "cw", label: "Female" }] as o (o.g)}
                  <button
                    onclick={() => setGender(o.g as "cm" | "cw")}
                    class="chamfer-btn py-2 font-display text-sm font-semibold uppercase tracking-wider transition-colors {gender ===
                    o.g
                      ? 'bg-surface-2 text-cyan glow-cyan'
                      : 'bg-void-2/50 text-ink-dim hover:text-ink'}"
                    style="--c:7px">{o.label}</button>
                {/each}
              </div>
            </div>
            <NeonButton full onclick={save} disabled={!ready}>Save Duelist</NeonButton>
          </div>
        </DataWindow>
      </aside>

      <!-- ── Catalog ──────────────────────────────────────────────────── -->
      <main in:fly={{ y: 24, duration: 440, delay: 80, opacity: 0 }} class="character-catalog">
        <DataWindow c={16}>
          {#if loadErr}
            <div class="catalog-height grid min-h-[520px] place-items-center p-8 text-center">
              <div class="flex max-w-xs flex-col items-center gap-3">
                <span class="text-3xl text-magenta">⚠</span>
                <p class="font-display text-sm font-semibold uppercase tracking-wider text-ink">Couldn't load customization</p>
                <p class="text-xs leading-relaxed text-ink-dim">The world server didn't respond. Check your connection and try again.</p>
                <NeonButton onclick={boot}>Retry</NeonButton>
              </div>
            </div>
          {:else if !manifest}
            <div class="catalog-height grid min-h-[520px] place-items-center">
              <span class="animate-pulse font-display text-xs uppercase tracking-[0.3em] text-ink-dim">Loading…</span>
            </div>
          {:else}
          <div class="catalog-height flex min-h-[520px] gap-0">
            <!-- category rail -->
            <nav class="category-rail flex w-[132px] shrink-0 flex-col gap-1 border-r border-cyan-dim/20 p-3">
              {#each CATS as cat (cat.key)}
                <button
                  onclick={() => (activeCat = cat.key)}
                  class="chamfer-btn flex items-center gap-2 px-3 py-2 text-left font-display text-xs font-semibold uppercase tracking-wider transition-colors {activeCat ===
                  cat.key
                    ? 'bg-surface-2 text-cyan glow-cyan'
                    : 'text-ink-dim hover:bg-void-2/60 hover:text-ink'}"
                  style="--c:6px"
                >
                  <span class="text-sm opacity-80">{cat.icon}</span>
                  <span class="truncate">{cat.label}</span>
                </button>
              {/each}
            </nav>

            <!-- item grid + colours -->
            <section class="catalog-content flex min-w-0 flex-1 flex-col gap-4 p-5">
              <div class="flex items-baseline justify-between">
                <h2 class="font-display text-lg font-bold uppercase tracking-[0.12em] text-ink">{activeMeta?.label ?? ""}</h2>
                <span class="font-display text-xs uppercase tracking-widest text-ink-dim">{categoryItemCount(activeCat)} items</span>
              </div>

              {#if activeCat === "sleeve"}
                <!-- Card-sleeve catalog, only sleeves the player OWNS (default + any
                     bought in the shop). Locked sleeves live in the shop, not here. -->
                <div class="item-grid grid flex-1 content-start gap-3" style="grid-template-columns:repeat(auto-fill,minmax(clamp(124px,9vw,168px),1fr))">
                  {#each visibleSleeves as s (s.id)}
                    {@const isSel = sleeve === s.id}
                    <button
                      onclick={() => (sleeve = s.id)}
                      class="group relative flex flex-col gap-1.5 overflow-hidden transition-all"
                      title={s.name}
                    >
                      <div
                        class="relative aspect-[59/86] w-full overflow-hidden chamfer border transition-all {isSel
                          ? 'border-cyan glow-cyan'
                          : 'border-cyan-dim/25 group-hover:border-cyan/50'}"
                        style="--c:7px"
                      >
                        <SleeveSwatch id={s.id} />
                        {#if isSel}
                          <span class="absolute bottom-1 right-1 grid size-5 place-items-center rounded-full bg-cyan text-[11px] font-bold text-void">✓</span>
                        {/if}
                      </div>
                      <div class="flex flex-col items-start leading-tight">
                        <span class="font-display text-[0.66rem] font-semibold text-ink">{s.name}</span>
                        <span class="font-display text-[0.55rem] uppercase tracking-wider {RARITY[s.rarity]}">{s.rarity}</span>
                      </div>
                    </button>
                  {/each}
                </div>
              {:else}
              <div class="item-grid grid flex-1 content-start gap-3" style="grid-template-columns:repeat(auto-fill,minmax(clamp(112px,8vw,168px),1fr))">
                {#each gridItems as item (item.slot + item.variant)}
                  {@const isSel = isItemSel(item)}
                  {@const rar = silhouetteRarity(item.slot, item.variant)}
                  {@const beta = duelDiskSkuFor(item.slot, item.variant, tileColor(item.slot, item.variant, isSel)) === BETA_GOLD_DUELDISK_SKU}
                  {@const bg = beta ? "bg-gold/15" : rar && rar !== "common" ? RARITY_BG[rar] : isSel ? "bg-cyan/10" : "bg-void-2/50"}
                  <button
                    onclick={() => pickItem(item)}
                    class="group relative grid aspect-square place-items-center overflow-hidden chamfer-btn border-2 p-px transition-all {bg} {isSel ? 'glow-cyan' : 'hover:border-cyan/50'} {beta ? 'border-gold shadow-[0_0_14px_rgba(255,179,64,0.4)]' : rar && !isSel ? RARITY_BORDER_REST[rar] : isSel ? (rar ? RARITY_BORDER[rar] : 'border-cyan') : 'border-cyan-dim/25'}"
                    style="--c:8px"
                    title={beta ? "Beta Exclusive" : undefined}
                  >
                    {#if item.variant === "00"}
                      <span class="font-display text-[11px] uppercase tracking-wider text-ink-dim">None</span>
                    {:else}
                      {@const z = tileZoom(item.slot, item.variant)}
                      <!-- Cel art is a wide 768-canvas layer. Tall parts contain to fit; small facial
                           features (eyes/nose/…) are zoomed from centre so they don't read as a sliver. -->
                      <img
                        src={thumb(item.slot, item.variant, tileColor(item.slot, item.variant, isSel))}
                        alt=""
                        class="max-h-full max-w-full object-contain {z === 1 ? 'p-1.5' : ''}"
                        style={tileTransform(z)}
                      />
                    {/if}
                    {#if beta}
                      <span class="absolute left-0 top-0 bg-gradient-to-r from-gold to-orange/90 px-1 font-display text-[7px] font-bold uppercase tracking-wider text-void">Beta</span>
                    {/if}
                    {#if isSel}
                      <span class="absolute bottom-1 right-1 grid size-4 place-items-center rounded-full bg-cyan text-[9px] font-bold text-void">✓</span>
                    {/if}
                  </button>
                {/each}
              </div>

              {#if colorOptions.length > 1}
                <div class="border-t border-cyan-dim/15 pt-3">
                  <span class="mb-2 block font-display text-xs font-semibold uppercase tracking-[0.14em] text-ink-dim">Colour</span>
                  <div class="flex flex-wrap gap-2">
                    {#each colorOptions as c (c)}
                      {@const cRar = colourRarity(colorSlot, colorChoice.variant, c)}
                      {@const cSel = colorChoice.color === c}
                      {@const cz = tileZoom(colorSlot, colorChoice.variant)}
                      <button
                        onclick={() => setActiveColor(c)}
                        class="size-[clamp(52px,4vw,68px)] grid place-items-center overflow-hidden chamfer-btn border-2 p-px transition-all {cSel ? 'bg-cyan/10 glow-cyan' : 'bg-void-2/50 hover:border-cyan/50'} {cRar && !cSel ? RARITY_BORDER_REST[cRar] : cSel ? (cRar ? RARITY_BORDER[cRar] : 'border-cyan') : 'border-cyan-dim/25'}"
                        style="--c:6px"
                        aria-label="colour {c}{cRar ? ` (${cRar})` : ''}"
                        title={cRar ?? undefined}
                      >
                        <img
                          src={thumb(colorSlot, colorChoice.variant, c)}
                          alt=""
                          class="max-h-full max-w-full object-contain {cz === 1 ? 'p-1' : ''}"
                          style={tileTransform(cz)}
                        />
                      </button>
                    {/each}
                  </div>
                </div>
              {/if}
              {/if}
            </section>
          </div>
          {/if}
        </DataWindow>
      </main>
    </div>
  </div>
</div>

<style>
  /* Landscape phones have ample width but very little height. Keep the live preview, identity
     controls, and scrollable catalog in one working viewport instead of stacking two tall panels. */
  @media (orientation: landscape) and (max-height: 600px) and (min-width: 640px) {
    .character-root {
      display: block;
      padding: 0.5rem;
    }
    .character-shell {
      width: 100%;
      gap: 0.4rem;
    }
    .character-header {
      min-height: 2rem;
      align-items: center;
    }
    .character-header h1 {
      font-size: 1.2rem;
      line-height: 1.35;
    }
    .character-grid {
      grid-template-columns: minmax(190px, 28vw) minmax(0, 1fr);
      gap: 0.5rem;
    }
    .character-sidebar {
      gap: 0.5rem;
    }
    .preview-content {
      gap: 0.25rem;
      padding: 0.35rem 0.65rem;
    }
    .preview-stage {
      width: min(100%, 5.25rem);
    }
    .identity-content {
      gap: 0.45rem;
      padding: 0.45rem 0.65rem;
    }
    .character-catalog {
      min-width: 0;
    }
    .catalog-height {
      height: calc(100dvh - 3.5rem);
      min-height: 0;
    }
    .category-rail {
      width: 6.75rem;
      padding: 0.4rem;
    }
    .category-rail button {
      padding: 0.4rem 0.5rem;
    }
    .catalog-content {
      min-height: 0;
      gap: 0.5rem;
      overflow: hidden;
      padding: 0.65rem;
    }
    .item-grid {
      min-height: 0;
      overflow-y: auto;
      padding-right: 0.2rem;
    }
  }
</style>
